import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { submitEmailOptInFeedback } from "../api/marketingEmailOptIn";

const parseToken = (search) => {
  const params = new URLSearchParams(search);
  return params.get("token") || "";
};

const reasonOptions = [
  "Too many emails",
  "Not relevant",
  "Prefer text messages",
  "Moved away",
  "Other",
];

export default function EmailSubscriptionFeedback() {
  const location = useLocation();
  const token = useMemo(() => parseToken(location.search), [location.search]);
  const [reason, setReason] = useState("");
  const [detail, setDetail] = useState("");
  const [preferences, setPreferences] = useState({
    promos: false,
    new_services: false,
    events: false,
    tips: false,
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      await submitEmailOptInFeedback({
        token,
        reason: reason || null,
        detail: detail || null,
        preferences_json: preferences,
      });
      setSuccess("Thanks for sharing your feedback.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to submit feedback.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8">
      <div className="mx-auto w-full max-w-[640px]">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              Subscription Feedback
            </h1>
          </div>
          <div className="px-6 py-6 space-y-4 text-sm md:text-base">
            {!token && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                Missing or invalid token.
              </div>
            )}
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {error}
              </div>
            )}
            {success && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700">
                {success}
              </div>
            )}

            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-500">
                Reason (optional)
              </label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
              >
                <option value="">Select a reason</option>
                {reasonOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-500">
                Additional details (optional)
              </label>
              <textarea
                rows="3"
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-2">
              <div className="text-xs font-semibold uppercase text-slate-500">
                Preferred content
              </div>
              {Object.keys(preferences).map((key) => (
                <label
                  key={key}
                  className="flex items-center gap-2 text-sm text-slate-700"
                >
                  <input
                    type="checkbox"
                    checked={Boolean(preferences[key])}
                    onChange={(e) =>
                      setPreferences((prev) => ({
                        ...prev,
                        [key]: e.target.checked,
                      }))
                    }
                    className="h-4 w-4 rounded border-slate-300"
                  />
                  {key.replace(/_/g, " ")}
                </label>
              ))}
            </div>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!token || loading}
              className="rounded-full bg-rose-500 px-4 py-2 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
            >
              {loading ? "Submitting..." : "Send Feedback"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
